import { SearchOutlined } from "@ant-design/icons";
import { Input, Space, Table, Typography } from "antd";
import { debounce } from "lodash";
import moment from "moment-timezone";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { connect, useDispatch, useSelector } from "react-redux";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import styled from "styled-components";

import ErrorMessage from "../../component/customComponent/customMessages/ErrorMessage";
import SuccessMessage from "../../component/customComponent/customMessages/SuccessMessage";

import { ApiNames, deleteSavedListAPI, getNumberOfPatientsAPI, parseSearchFormParams } from "../../api/api";
import ACTION from "../../store/action";
import {
  selectIsFetching,
  selectProvidersFacility,
  selectSavedListData,
  selectTableColumns,
  selectedFilterListQuery,
  selectedFilterListType,
  selectedFilterSorting,
  selectedSavedListActiveColumn
} from "../../store/selector";

import { ASCEND, DECEND, apiSortOptions } from "../../constant/sort";
import { dateSort, stringSort } from "../../utils/sort";
import { TailormedSpinner } from "../TailormedSpinner/TailormedSpinner";
import { ListNameColumn, NumberOfRecordsColumn } from "./CommonColumns";

import { fontWeights, sizes } from "../../constant/styles";
import { SAVED_LIST_PAGE_SIZE, selectedViews } from "../../constant/table";

import { ReactComponent as DeleteIcon } from "../../assets/svg/delete-new-icon.svg";
import { ReactComponent as NoFavoriteListFoundIcon } from "../../assets/svg/no-favorite-list-found.svg";
import { ReactComponent as NoSavedListFoundIcon } from "../../assets/svg/no-saved-list-found.svg";
import { ReactComponent as NoSharedListFoundIcon } from "../../assets/svg/no-shared-list-found.svg";

import { filtersTypes, filtersTypesOptions, SAVED_LIST_SOURCES } from "../../constant/savedList";
import { isExpressEnrollList } from "../../utils/savedList";
import { EXPRESS_GREEN_ENROLL } from "../../constant/colors";
import { RadioGroup } from "../UI/Radio/Group";

import { useHistory, useLocation } from "react-router-dom";
import { DARK_GRAY3 } from "../../constant/colors";
import { defaultSavedListFilters } from "../../constant/filters";
import { Routes } from "../../constant/routes";
import { scrollTableToTop } from "../../utils/fetchTableData";
import { ModalComponent } from "../ModalComponent";
import { SavedListActionsMenu } from "../SavedList/SavedListActionsMenu";
import LinkableCell from "../UI/LinkableCell";
import { StarFilterListColumn } from "./CommonColumns";
import SavedListPageCount from "./SavedListPageCount";
import { commonTableStyle } from "./TableStyle";
import {
  useStandardizedTracking,
  EventNames,
  ContentTypes,
  LayoutTypes,
  FeatureGroups,
  PageKeys,
  EEContentKeys
} from "../../utils/pendo/useStandardizedTracking";

const { Text } = Typography;

const StyledNoListSpace = styled(Space)`
  margin-left: -20px;
`;
const StyledNoListTitle = styled(Text)`
  font-weight: ${fontWeights.semibold};
  font-size: ${sizes.large};
`;
const StyledNoListDescription = styled(Text)`
  color: ${DARK_GRAY3};
  font-size: ${sizes.medium};
  white-space: pre-line;
`;

const StyledTable = styled(Table)`
  ${commonTableStyle()}
  .ant-table-empty {
    margin-top: 10px;
  }

  .ant-table-column-sorter {
    margin-left: 10px;
  }

  .ant-table-tbody > tr > td:nth-child(2) {
    padding-left: 10px !important;
  }

  .ant-table-tbody > tr.ee-list-row > td {
    background-color: ${EXPRESS_GREEN_ENROLL} !important;
  }

  .ant-table-tbody > tr.ee-list-row:hover > td {
    background-color: ${EXPRESS_GREEN_ENROLL} !important;
  }
`;

const path = ({ id }) => `${Routes.FILTER_LIST}/${id}`;

const renderWithLink = (patientRec, renderFn) => (
  <LinkableCell to={{ pathname: path(patientRec), state: { from: selectedViews.savedList } }}>
    {renderFn()}
  </LinkableCell>
);

const SavedListTableComponent = ({
  tableColumns,
  isFetching,
  tableData,
  searchSavedListAct,
  setListNameQueryAct,
  setSelectedFilterTypeAct,
  selectedListType,
  setSavedListSortByAct,
  setSavedListOrderByAct,
  activeTabSorting,
  searchQuery,
  activeColumn,
  t
}) => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { track, trackOnce } = useStandardizedTracking();

  const searchParams = new URLSearchParams(location.search);
  const initialPage = parseInt(searchParams.get("page"), 10) || 1;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  // The URL ?page param is the source of truth for the current page; table state is the fallback.
  // The index opts out of the Redux page-restore the bucket views use because its table state is
  // cleared when leaving for a record, so only the URL reliably carries the page across the trip.
  const [currentPage, setCurrentPage] = useState(
    initialPage > 1 ? initialPage : tableData?.selectedPage ?? initialPage
  );
  const [savedListsRecordCounts, setSavedListsRecordCounts] = useState({});

  const accessibleFacilities = useSelector(selectProvidersFacility);
  const accessibleFacilityIds = useMemo(
    () => new Set((accessibleFacilities || []).map((f) => f.id)),
    [accessibleFacilities]
  );

  const sortTooltips = {
    [ASCEND]: t("savedList.sort_descend_tooltip"),
    [DECEND]: t("savedList.sort_cancel_tooltip"),
    null: t("savedList.sort_ascend_tooltip")
  };

  const getNextSortTooltip = (columnName) => sortTooltips[getSortOrder(columnName)];

  const handlePageChange = (page) => {
    setCurrentPage(page);
    searchSavedListAct(page);
    // Keep the page in the URL so a browser-back round-trip restores it without relying on the
    // index's Redux table state, which does not survive leaving for a record and returning.
    const params = new URLSearchParams(location.search);
    params.set("page", page);
    history.replace({ search: params.toString() });
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (tableData?.records) {
      trackOnce("saved-lists-page-impression", () => {
        const eeListsCount = tableData.records?.filter((list) => isExpressEnrollList(list)).length || 0;

        track(EventNames.PAGE_IMPRESSION, {
          pageKey: PageKeys.SAVED_LISTS,
          featureGroup: FeatureGroups.SAVED_LISTS,
          contentType: ContentTypes.PATIENT,
          contentKey: EEContentKeys.SAVED_LISTS_PAGE,
          additionalInfo: {
            total_lists_count: tableData.records?.length || 0,
            ee_lists_count: eeListsCount
          }
        });
      });
    }
  }, [tableData?.records, track, trackOnce]);

  const handleEESavedListClick = (record) => {
    if (isExpressEnrollList(record)) {
      track(EventNames.BUTTON_CLICK, {
        pageKey: PageKeys.SAVED_LISTS,
        featureGroup: FeatureGroups.EXPRESS_ENROLLMENT,
        contentType: ContentTypes.PATIENT,
        contentKey: EEContentKeys.EE_SAVED_LIST_CLICK,
        layout: LayoutTypes.TABLE,
        additionalInfo: {
          list_id: record.id,
          list_name: record.name,
          is_ee_list: true,
          list_type: record.type,
          patient_count: record.patientsCount
        }
      });
    }
  };

  useEffect(() => {
    searchSavedListAct(currentPage);
  }, [selectedListType, searchQuery]);

  const fetchDataCount = async (savedList) => {
    const selectedView =
      savedList.selectedView === selectedViews.highPriority ? selectedViews.topOpportunity : savedList.selectedView;
    const params = typeof savedList?.params === "string" ? JSON.parse(savedList?.params) : savedList?.params;

    // Remove facility IDs that no longer exist in user's accessible facilities
    const filters = { ...params?.filters };
    if (filters.multiples?.facilities && Object.keys(filters.multiples.facilities).length > 0) {
      const filteredFacilities = {};
      Object.entries(filters.multiples.facilities).forEach(([key, facility]) => {
        if (accessibleFacilityIds.has(facility.value)) {
          filteredFacilities[key] = facility;
        }
      });
      filters.multiples = { ...filters.multiples, facilities: filteredFacilities };
    }

    const formData = parseSearchFormParams({
      selectedView: selectedView,
      page: defaultSavedListFilters.skip,
      limit: defaultSavedListFilters.limit,
      filters
    });

    try {
      const { data } = await getNumberOfPatientsAPI(
        formData?.filters,
        defaultSavedListFilters.skip,
        defaultSavedListFilters.limit,
        selectedView
      );

      const count = data?.totalPatients;
      setSavedListsRecordCounts((prevState) => ({
        ...prevState,
        [savedList.id]: {
          success: true,
          total: count
        }
      }));
      dispatch(ACTION.setSavedListRecordCount(savedList.id, count));
    } catch (error) {
      setSavedListsRecordCounts((prevState) => ({
        ...prevState,
        [savedList.id]: {
          total: null,
          success: false
        }
      }));
      dispatch(ACTION.setSavedListRecordCount(savedList.id, null));
    }
  };

  useEffect(() => {
    if (tableData?.data) {
      tableData.data.forEach(fetchDataCount);
    }
  }, [tableData]);

  const getSortOrder = (columnName) => {
    if (!activeColumn || activeColumn.name !== columnName || !activeTabSorting) return null;
    return activeTabSorting[columnName] === apiSortOptions.asc ? ASCEND : DECEND;
  };

  const columns = [
    {
      dataIndex: tableColumns[selectedViews.savedList]?.star?.dataIndex,
      width: 35,
      key: tableColumns[selectedViews.savedList]?.star?.key,
      render: (text, record) => <StarFilterListColumn key={record.id} page={currentPage} record={record} />
    },
    {
      ...tableColumns[selectedViews.savedList]?.name,
      render: (text, record) => renderWithLink(record, () => <ListNameColumn record={record} />),
      sorter: {
        compare: (a, b, direction) => stringSort(a.name, b.name, direction)
      },
      sortDirections: [ASCEND, DECEND, null],
      sortOrder: getSortOrder("name"),
      showSorterTooltip: {
        placement: "top",
        title: getNextSortTooltip("name"),
        align: { targetOffset: ["45%", "0"] }
      }
    },
    {
      ...tableColumns[selectedViews.savedList]?.updatedAt,
      render: (text, record) => renderWithLink(record, () => moment(record.updatedAt).format("MMM D, YYYY")),
      sorter: {
        compare: (a, b, direction) => dateSort(a.updatedAt, b.updatedAt, direction)
      },
      width: 150,
      sortDirections: [ASCEND, DECEND, null],
      sortOrder: getSortOrder("updatedAt"),

      showSorterTooltip: {
        placement: "top",
        title: getNextSortTooltip("updatedAt"),
        align: { targetOffset: ["10%", "0"] }
      }
    },
    {
      dataIndex: tableColumns[selectedViews.savedList]?.numberOfRecords,
      key: tableColumns[selectedViews.savedList]?.numberOfRecords?.key,
      title: tableColumns[selectedViews.savedList]?.numberOfRecords?.title,
      width: 150,
      render: (text, record) =>
        renderWithLink(record, () => (
          <NumberOfRecordsColumn record={record} savedListsRecordCounts={savedListsRecordCounts} />
        ))
    },
    {
      ...tableColumns[selectedViews.savedList]?.actions?.tableColumns,
      width: 10,
      render: (text, record) => (
        <SavedListActionsMenu
          record={record}
          recordCount={savedListsRecordCounts[record.id]?.total}
          deleteAction={() => {
            setSelectedRow(record);
            setShowDeleteModal(true);
          }}
          source={SAVED_LIST_SOURCES.SAVED_LISTS}
        />
      )
    }
  ];

  const onFilterChange = (e) => {
    setCurrentPage(1);
    setSelectedFilterTypeAct(e.target.value);
  };
  const onTableChange = (pagination, filters, sorter) => {
    const { current: nextPage } = pagination;
    if (nextPage === currentPage) {
      const sortedColumns = Array.isArray(sorter) ? sorter : [sorter];

      if (!sortedColumns.length || !sortedColumns[0].order) {
        setCurrentPage(1);
        setSavedListOrderByAct(null);
        setSavedListSortByAct(null);
        searchSavedListAct(1);
        return;
      }

      const activeSortedColumn = sortedColumns[0];
      let { order: nextSortBy, column: { key: nextColumnName } = {} } = activeSortedColumn;

      nextColumnName = nextColumnName === "listName" ? "name" : "updatedAt";
      const columnIschanged = nextColumnName !== (activeColumn?.name || "");

      nextSortBy = nextSortBy === DECEND ? apiSortOptions.desc : apiSortOptions.asc;
      const prevSortBy = activeTabSorting?.[nextColumnName];
      const sortDirectionIschanged = prevSortBy !== nextSortBy;

      if (columnIschanged || sortDirectionIschanged) {
        setCurrentPage(1);
        setSavedListOrderByAct(nextColumnName);
        setSavedListSortByAct(nextSortBy);
        searchSavedListAct(1);
      }
    }
    scrollTableToTop();
  };

  const onSearchHandler = (e) => {
    setCurrentPage(1);
    setListNameQueryAct(e.target.value);
  };

  const debouncedSearchChangeHandler = useCallback(debounce(onSearchHandler, 500), []);

  return (
    <div style={{ width: "100%" }}>
      <ModalComponent
        open={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        okText={t("savedList.confirm_modal.ok_text")}
        onOk={async () => {
          try {
            const deleteListRes = await deleteSavedListAPI(selectedRow?.id);
            if (deleteListRes) {
              let nextExpectedPage = currentPage;
              const isLastRecord = tableData.total - 1 === 0;
              if ((tableData.total - 1) % SAVED_LIST_PAGE_SIZE === 0 && !isLastRecord && currentPage > 1)
                nextExpectedPage = currentPage - 1;

              setCurrentPage(nextExpectedPage);

              searchSavedListAct(nextExpectedPage);

              setShowDeleteModal(false);

              SuccessMessage(t("savedList.confirm_modal.success_message"));
            }
          } catch (error) {
            ErrorMessage(t("savedList.confirm_modal.error_message"));
          }
        }}
      >
        <Space direction="vertical">
          <DeleteIcon style={{ width: "48px" }} />
          <Text className="title">{t("savedList.confirm_modal.title")}</Text>
          <Text className="content">
            {t("savedList.confirm_modal.content", {
              listName: selectedRow?.name
            })}
          </Text>
        </Space>
      </ModalComponent>

      <Space style={{ justifyContent: "space-between", display: "flex" }}>
        <Input
          style={{ width: "400px" }}
          placeholder={t("savedList.search_placeholder")}
          defaultValue={searchQuery}
          allowClear
          prefix={<SearchOutlined />}
          id="saved_lists_search_bar"
          onChange={debouncedSearchChangeHandler}
        />
        <RadioGroup
          onChange={onFilterChange}
          options={filtersTypesOptions}
          defaultValue={selectedListType || filtersTypes.all}
          buttonStyle="solid"
          t={t}
          id="saved_lists_all_options_lists"
        />
      </Space>

      <StyledTable
        size="small"
        rowClassName={(record) => {
          const classes = [];
          if (!record.isValid) {
            classes.push("disabled-row");
          } else {
            classes.push("cursor-pointer");
          }
          if (isExpressEnrollList(record)) {
            classes.push("ee-list-row");
          }
          return classes.join(" ");
        }}
        onRow={(record) => ({
          onClick: () => handleEESavedListClick(record)
        })}
        loading={{ indicator: <TailormedSpinner />, spinning: isFetching }}
        columns={columns}
        onChange={onTableChange}
        locale={{
          emptyText:
            tableData &&
            tableData.data?.length == 0 &&
            (selectedListType === filtersTypes.starred ? (
              <StyledNoListSpace direction={"vertical"} size={10}>
                <NoFavoriteListFoundIcon />
                <StyledNoListTitle>
                  {t(searchQuery === "" ? "no_favorite_to_show" : "no_saved_lists_found")}
                </StyledNoListTitle>
                {searchQuery === "" && (
                  <StyledNoListDescription>{t("no_favorite_description")}</StyledNoListDescription>
                )}
              </StyledNoListSpace>
            ) : selectedListType === filtersTypes.shared ? (
              <StyledNoListSpace direction={"vertical"} size={10}>
                <NoSharedListFoundIcon />
                <StyledNoListTitle>
                  {t(searchQuery === "" ? "no_shared_lists_to_show" : "no_saved_lists_found")}
                </StyledNoListTitle>
                {searchQuery === "" && (
                  <StyledNoListDescription>{t("no_shared_lists_to_show_description")}</StyledNoListDescription>
                )}
              </StyledNoListSpace>
            ) : (
              <StyledNoListSpace direction={"vertical"} size={10}>
                <NoSavedListFoundIcon />
                <StyledNoListTitle>
                  {t(searchQuery === "" ? "no_saved_lists_to_show" : "no_saved_lists_found")}
                </StyledNoListTitle>
                {searchQuery === "" && (
                  <StyledNoListDescription>{t("no_saved_lists_to_show_description")}</StyledNoListDescription>
                )}
              </StyledNoListSpace>
            ))
        }}
        pagination={{
          size: "small",
          showSizeChanger: false,
          current: currentPage,
          onChange: handlePageChange,
          defaultPageSize: SAVED_LIST_PAGE_SIZE,
          pageSize: SAVED_LIST_PAGE_SIZE,
          position: ["bottom"],
          total: tableData?.total,
          showTotal: (total, range) => (
            <div style={{ display: "flex", flexDirection: "row" }}>
              <SavedListPageCount total={total} range={range} isAccurateCount={true} />
            </div>
          )
        }}
        dataSource={
          tableData && tableData.data.length > 0
            ? tableData.data
                .sort((a, b) => b.isActive - a.isActive)
                .map((list, index) => {
                  return {
                    ...list,
                    key: index
                  };
                })
            : null
        }
      />
    </div>
  );
};

const mapStateToProps = createStructuredSelector({
  tableColumns: selectTableColumns,
  isFetching: selectIsFetching([ApiNames.searchSavedList]),
  tableData: selectSavedListData,
  selectedListType: selectedFilterListType,
  searchQuery: selectedFilterListQuery,
  activeTabSorting: selectedFilterSorting,
  activeColumn: selectedSavedListActiveColumn
});

const mapDispatchToProps = (dispatch) => ({
  searchSavedListAct: (page) => dispatch(ACTION.searchSavedListAction(page)),
  setListNameQueryAct: (query) => dispatch(ACTION.setListNameQueryAction(query)),
  setSelectedFilterTypeAct: (listType) => dispatch(ACTION.setSelectedFilterListType(listType)),
  setSavedListSortByAct: (sortBy) => dispatch(ACTION.updateSavedListSortBy(sortBy)),
  setSavedListOrderByAct: (orderBy) => dispatch(ACTION.updateSavedListOrderBy(orderBy))
});

const SavedListTable = compose(
  withTranslation(),
  connect(mapStateToProps, mapDispatchToProps)
)(SavedListTableComponent);

export { SavedListTable };
